import React, { useEffect, useState } from 'react';

export interface MenuBarProps {
    shutdown: () => void;
}

const getTime = () => {
    const date = new Date();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const amPm = hours >= 12 ? 'PM' : 'AM';
    const h = hours % 12 || 12;
    const mins = minutes < 10 ? '0' + minutes : minutes;
    return `${h}:${mins} ${amPm}`;
};

const getDate = () => {
    return new Date().toLocaleDateString('en-US', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    });
};

const MenuBar: React.FC<MenuBarProps> = ({ shutdown }) => {
    const [time, setTime] = useState(getTime());
    const [date, setDate] = useState(getDate());
    const [appleMenuOpen, setAppleMenuOpen] = useState(false);
    const menuRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const id = setInterval(() => {
            setTime(getTime());
            setDate(getDate());
        }, 5000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setAppleMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const menuItems = ['Finder', 'File', 'Edit', 'View', 'Go', 'Window', 'Help'];

    return (
        <div style={styles.bar}>
            <div style={styles.left}>
                <div
                    ref={menuRef}
                    style={styles.appleMenuTrigger}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        setAppleMenuOpen(!appleMenuOpen);
                    }}
                >
                    <span style={styles.appleLogo}>&#63743;</span>
                </div>
                {appleMenuOpen && (
                    <div style={styles.appleDropdown}>
                        <div
                            style={styles.appleDropdownItem}
                            onMouseDown={() => {
                                shutdown();
                                setAppleMenuOpen(false);
                            }}
                        >
                            Shut Down...
                        </div>
                    </div>
                )}
                {menuItems.map((item) => (
                    <span key={item} style={styles.menuItem}>
                        {item}
                    </span>
                ))}
            </div>
            <div style={styles.right}>
                <span style={styles.statusIcon} title="Bluetooth">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.71 7.71L12 2h-1v7.59L6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 11 14.41V22h1l5.71-5.71-4.3-4.29 4.3-4.29zM13 5.83l1.88 1.88L13 9.59V5.83zm0 8.34v-3.76l1.88 1.88L13 14.17z" />
                    </svg>
                </span>
                <span style={styles.statusIcon} title="Wi-Fi">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.7 2.6 7.3 2.6 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z" />
                    </svg>
                </span>
                <span style={styles.statusIcon} title="Search">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                    </svg>
                </span>
                <span style={styles.statusIcon} title="Control Center">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                    </svg>
                </span>
                <span style={styles.date}>{date}</span>
                <span style={styles.time}>{time}</span>
            </div>
        </div>
    );
};

const styles: StyleSheetCSS = {
    bar: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 12,
        paddingRight: 12,
        background: 'rgba(30, 30, 35, 0.72)',
        backdropFilter: 'blur(20px) saturate(150%)',
        WebkitBackdropFilter: 'blur(20px) saturate(150%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 13,
        color: 'rgba(255, 255, 255, 0.95)',
        zIndex: 99999,
    },
    left: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        gap: 2,
    },
    appleMenuTrigger: {
        cursor: 'pointer',
        padding: '2px 6px',
        borderRadius: 4,
        marginRight: 4,
    },
    appleLogo: {
        fontSize: 16,
        lineHeight: 1,
        color: 'rgba(255, 255, 255, 0.95)',
    },
    menuItem: {
        padding: '2px 8px',
        cursor: 'default',
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.95)',
    },
    appleDropdown: {
        position: 'absolute',
        top: 24,
        left: 0,
        minWidth: 180,
        background: 'rgba(40, 40, 45, 0.88)',
        backdropFilter: 'blur(20px)',
        borderRadius: 8,
        boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
        padding: '6px 0',
        zIndex: 100001,
        border: '1px solid rgba(255,255,255,0.08)',
    },
    appleDropdownItem: {
        padding: '6px 16px',
        cursor: 'pointer',
        color: 'rgba(255, 255, 255, 0.95)',
    },
    right: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    statusIcon: {
        display: 'flex',
        alignItems: 'center',
        color: 'rgba(255, 255, 255, 0.95)',
        opacity: 0.95,
    },
    date: {
        fontSize: 13,
        fontWeight: 400,
        marginRight: 4,
        color: 'rgba(255, 255, 255, 0.95)',
    },
    time: {
        fontSize: 13,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.95)',
    },
};

export default MenuBar;
